/**
 * Email Validator
 *
 * Single source of truth for email-address validation across the web project.
 *
 * Mirrors Flutter's `RegexUtils` exactly:
 * - `EMAIL_REGEX` is the verbatim port of `RegexUtils.regexEmail`
 *   (`lib/presentation/utils/regex_utils.dart:2` — raw Dart string
 *   `r'\S+@\S+\.\S+'`).
 * - `isValidEmailAddress` mirrors `RegexUtils.isValidEmailAddress`
 *   (`lib/presentation/utils/regex_utils.dart:4-6`).
 *
 * Used by the Contact page (and the future Support page port) — kept as a
 * dedicated owner file so additional consumers can share the predicate
 * without duplicating the regex literal.
 */

/**
 * Email regex literal, ported verbatim from Flutter's
 * `RegexUtils.regexEmail = r'\S+@\S+\.\S+'`.
 *
 * Requires: at least one non-whitespace character, an `@`, at least one
 * non-whitespace character, a literal `.`, and at least one trailing
 * non-whitespace character.
 */
export const EMAIL_REGEX = /\S+@\S+\.\S+/;

/**
 * Return `true` when `emailAddress` matches `EMAIL_REGEX`, otherwise `false`.
 *
 * Mirrors Flutter's `RegExp(regexEmail).hasMatch(...)` semantics — this is a
 * partial-match check (the address need only *contain* a substring matching
 * the regex). This is intentional, to keep parity with Flutter exactly.
 */
export function isValidEmailAddress(emailAddress: string): boolean {
  return EMAIL_REGEX.test(emailAddress);
}
